/**
 * Who is logged in, and what they may do.
 *
 * The role here drives *visibility only*. The server enforces permissions on
 * every request regardless — `require_edit` / `require_admin` on the routes,
 * and the DAO guards behind them. Hiding a button is a courtesy so a viewer
 * is not invited to click something that will fail; it is never the control.
 * Never move a check out of the API on the strength of a hidden button.
 */
import { createContext, useCallback, useContext, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '../api/client'
import type { Bootstrap, User } from '../api/types'

interface AuthValue {
  user: User | null
  /** True while we still do not know whether there is a session. */
  loading: boolean
  /** Whether this installation still needs its first administrator. */
  needsFirstAdmin: boolean
  canEdit: boolean
  canDelete: boolean
  isAdmin: boolean
  /**
   * A viewer gets money fields nulled by the server (DP-01, `webapi/redact.py`)
   * rather than a stripped-down response shape — the same field, `null`
   * instead of a number. Screens use this to render that null as the app's
   * "no value" convention (the dash) instead of letting arithmetic on it
   * produce a stray `0`, and to skip requests the server would 403 anyway
   * (`GET /dealers/{id}/prices`).
   */
  isViewer: boolean
  login: (username: string, password: string) => Promise<void>
  createFirstAdmin: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient()

  const bootstrap = useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api.get<Bootstrap>('/auth/bootstrap'),
    staleTime: Infinity,
  })

  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me')
      } catch (err) {
        // A 401 here is the normal "not logged in yet" case, not a failure.
        // Returning null keeps it out of the error path so the login screen
        // renders instead of an error boundary.
        if (err instanceof ApiError && err.isAuth) return null
        throw err
      }
    },
    retry: false,
    staleTime: 60_000,
  })

  const user = me.data ?? null

  const login = useCallback(
    async (username: string, password: string) => {
      const logged = await api.post<User>('/auth/login', { username, password })
      qc.setQueryData(['me'], logged)
      // Everything cached belonged to whoever was here before. Roles differ,
      // and so does what the server will return.
      await qc.invalidateQueries()
    },
    [qc],
  )

  const createFirstAdmin = useCallback(
    async (username: string, password: string) => {
      const created = await api.post<User>('/auth/first-admin', { username, password })
      qc.setQueryData(['me'], created)
      qc.setQueryData(['bootstrap'], { needs_first_admin: false })
      await qc.invalidateQueries()
    },
    [qc],
  )

  const logout = useCallback(async () => {
    await api.post<{ detail: string }>('/auth/logout')
    qc.setQueryData(['me'], null)
    // Clear, not invalidate: invalidating would refetch every active query as
    // an anonymous user and paint a screen full of 401s on the way out.
    qc.clear()
  }, [qc])

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading: me.isLoading || bootstrap.isLoading,
      needsFirstAdmin: bootstrap.data?.needs_first_admin ?? false,
      canEdit: user?.role === 'admin' || user?.role === 'editor',
      canDelete: user?.role === 'admin',
      isAdmin: user?.role === 'admin',
      isViewer: user?.role === 'viewer',
      login,
      createFirstAdmin,
      logout,
    }),
    [user, me.isLoading, bootstrap.isLoading, bootstrap.data, login, createFirstAdmin, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

export const ROLE_LABEL_HE: Record<string, string> = {
  admin: 'מנהל',
  editor: 'עורך',
  viewer: 'צופה',
}
